import "../styles/globals.css";
import { useEffect } from "react";
import PropTypes from "prop-types";
import Head from "next/head";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider } from "@emotion/react";
import theme from "@src/theme";
import createEmotionCache from "@src/createEmotionCache";
import { Provider } from "react-redux";
import store from "@/redux/store";
import { loadState, loadBookingInfoState } from "@/redux/localStorage";
import { setCart } from "@/redux/cart.slice";
import { addInfo } from "@/redux/book.slice";
import { setHydrated } from "@/redux/hydration.slice";
import Script from "next/script";

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

export default function MyApp(props) {
    const {
        Component,
        emotionCache = clientSideEmotionCache,
        pageProps,
    } = props;

    // Runs after the initial (SSR-matching) render, so the persisted cart/bkInfo
    // from localStorage never causes a hydration mismatch on first paint.
    useEffect(() => {
        const cart = loadState();
        const bkInfo = loadBookingInfoState();
        if (cart?.cart) store.dispatch(setCart(cart.cart));
        if (bkInfo) store.dispatch(addInfo(bkInfo));
        store.dispatch(setHydrated());
    }, []);

    return (
        <CacheProvider value={emotionCache}>
            <Provider store={store}>
                <Head>
                    <meta
                        name="viewport"
                        content="initial-scale=1, width=device-width"
                    />
                </Head>
                <ThemeProvider theme={theme}>
                    {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                    <CssBaseline />
                    <Component {...pageProps} />
                </ThemeProvider>
            </Provider>
            <Script
                id="gtag-init"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','GTM-T93DXZT');
                  `,
                }}
            />
        </CacheProvider>
    );
}

MyApp.propTypes = {
    Component: PropTypes.elementType.isRequired,
    emotionCache: PropTypes.object,
    pageProps: PropTypes.object.isRequired,
};
